import type { ValueEvent } from '@adornis/base/utilTypes';
import { css } from '@adornis/chemistry/directives/css';
import '@adornis/chemistry/elements/components/stack/x-stack.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view';
import { stateful, useMemo, useObservable, useState } from '@adornis/functional-lit/stateful';
import { useQuery } from '@adornis/functional-lit/use-query';
import { webcomponent } from '@adornis/functional-lit/webcomponent';
import { goTo } from '@adornis/router/client/open-href';
import { currentUser } from '@adornis/users/client/currentUser';
import { html, nothing } from 'lit';
import { until } from 'lit/directives/until.js';
import { DateTime } from 'luxon';
import '../../../client/formfields/d-product-picker';
import '../../../client/theme/d-dropdown-multiselection';
import { LASUser } from '../../../db/las-user';
import { Permissions } from '../../../db/permissions';
import { getUsersByPermission } from '../../users/api/getUsersByPermission';
import { UserPicker } from '../../users/client/UserPicker';
import { changeAssignee } from '../api/changeAssignee';
import { changeDraftStatus } from '../api/changeDraftStatus';
import { subscribeReviewDrafts } from '../api/subscribeReviewDrafts';
import { Draft, DraftStatus, DraftStatusKeys, DraftStatusScheduledDeploy } from '../db/Draft';
import { DraftListFilter } from '../db/DraftListFilter';
import { PublishDraftDialog } from './PublishDraftDialog';

const AcceptButton = stateful(
  ({ onClick, title, icon }: { onClick: () => Promise<void>; title: string; icon: string }) => {
    const [accept, setAccept] = useState(false);

    if (accept) {
      return html`
        <button
          class="btn btn-sm btn-circle btn-ghost btn-error"
          @click=${async () => {
            setAccept(false);
          }}
        >
          <d-icon solid> x </d-icon>
          <d-tooltip> Abbrechen </d-tooltip>
        </button>
        <button
          class="btn btn-sm btn-circle btn-ghost btn-accent"
          @click=${async () => {
            await onClick();
            setAccept(false);
          }}
        >
          <d-icon solid> ${icon} </d-icon>
          <d-tooltip> ${title} </d-tooltip>
        </button>
      `;
    }

    return html`
      <button class="btn btn-sm btn-circle btn-ghost" @click=${() => setAccept(true)}>
        <d-icon solid> ${icon} </d-icon>
        <d-tooltip> ${title} </d-tooltip>
      </button>
    `;
  },
);

export const ReviewDraftsList = webcomponent(
  'review-drafts-list',
  ({
    draftClassName,
    referenceID,
    getCommentDraftPath,
    addInfoToDraft,
  }: {
    referenceID?: string;
    draftClassName: string;
    getCommentDraftPath: (draft: Draft) => string;
    addInfoToDraft?: (draft: Draft) => Promise<any>;
  }) => {
    const [filter, setFilter] = useState(new DraftListFilter({ referenceID, draftClassName }));
    const user = useObservable(() => currentUser<LASUser>(), []);
    const { value: designers } = useQuery(
      () => getUsersByPermission(Permissions.DesignerPermission.EDIT)({ _id: 1, email: 1 }),
      [],
    );
    const { value: reviewers } = useQuery(
      () => getUsersByPermission(Permissions.RedakteurPermission.EDIT)({ _id: 1, email: 1 }),
      [],
    );

    if (!user || !designers || !reviewers) return nothing;

    const renderStatusBadge = (status?: DraftStatus) => {
      const statusValue = status?.name ?? 'IN_PROGRESS';
      const statusMap = {
        IN_REVIEW: { class: 'badge badge-info', text: 'In Korrektur' },
        IN_REVISION: { class: 'badge badge-secondary', text: 'In Überarbeitung' },
        PUBLISHED: { class: 'badge badge-success', text: 'Veröffentlicht' },
        SCHEDULED_DEPLOY: { class: 'badge badge-success', text: 'Geplante Veröffentlichung' },
      };

      const statusInfo = statusMap[statusValue] || { class: 'badge', text: DraftStatusKeys[statusValue] };

      return html`
        <span class="${statusInfo.class} whitespace-nowrap" style="display: table">
          ${statusInfo.text}
          <br />
          ${status instanceof DraftStatusScheduledDeploy
            ? html` <span class="text-xs"> am ${status.deployAt.toLocaleString(DateTime.DATETIME_MED)} </span> `
            : nothing}
        </span>
      `;
    };

    const renderActionButtons = (draft: Draft) => {
      const editButton = html`
        <button
          class="btn btn-sm btn-circle btn-ghost"
          title="Bearbeiten"
          @click=${async () => {
            goTo(getCommentDraftPath(draft));
          }}
        >
          <d-icon solid> pencil </d-icon>
          <d-tooltip> Bearbeiten </d-tooltip>
        </button>
      `;
      const deleteButton =
        draft.createdBy === user._id
          ? AcceptButton({
              onClick: async () => {
                await draft.remove();
                XSnackbar.show('Entwurf gelöscht!');
              },
              title: 'Löschen',
              icon: 'trash',
            })
          : nothing;

      if (
        draft.statusKey === 'IN_PROGRESS' &&
        ((draft.assignedDesigner === user._id && user.hasPermission(Permissions.DesignerPermission.EDIT)) ||
          user.hasRole(Permissions.UserRoles.SUPER_ADMIN))
      )
        return html`
          <div class="flex gap-2 justify-end">
            ${AcceptButton({
              onClick: async () => {
                await changeDraftStatus(draft._id, 'IN_REVIEW')();
                XSnackbar.show('Zur Korrektur freigegeben!');
              },
              title: 'Zur Korrektur freigeben',
              icon: 'message-check',
            })}
            ${editButton} ${deleteButton}
          </div>
        `;

      if (
        draft.statusKey === 'IN_REVISION' &&
        ((draft.assignedDesigner === user._id && user.hasPermission(Permissions.DesignerPermission.EDIT)) ||
          user.hasRole(Permissions.UserRoles.SUPER_ADMIN))
      ) {
        return html`
          <div class="flex gap-2 justify-end flex-wrap">
            ${AcceptButton({
              onClick: async () => {
                await changeDraftStatus(draft._id, 'IN_REVIEW')();
                XSnackbar.show('Zurück in Korrektur verschoben!');
              },
              title: 'Zurück in Korrektur',
              icon: 'rotate-left',
            })}
            ${editButton}
            <button
              class="btn btn-sm btn-circle btn-ghost btn-accent"
              title="Bearbeiten"
              @click=${async () => {
                await PublishDraftDialog(draft);
              }}
            >
              <d-icon solid> upload </d-icon>
              <d-tooltip> Veröffentlichen </d-tooltip>
            </button>
            ${deleteButton}
          </div>
        `;
      }

      if (
        draft.statusKey === 'IN_REVIEW' &&
        ((draft.assignedReviewer === user._id && user.hasPermission(Permissions.RedakteurPermission.EDIT)) ||
          user.hasRole(Permissions.UserRoles.SUPER_ADMIN))
      ) {
        return html`
          <div class="flex gap-2 justify-end flex-wrap">
            <button
              class="btn btn-sm btn-circle btn-ghost"
              title="Kommentieren"
              @click=${async () => {
                if (draft.statusKey !== 'IN_REVIEW') {
                  await changeDraftStatus(draft._id, 'IN_REVIEW')();
                }
                goTo(getCommentDraftPath(draft));
              }}
            >
              <d-icon solid> message-check </d-icon>
              <d-tooltip> Kommentieren </d-tooltip>
            </button>
            ${AcceptButton({
              onClick: async () => {
                await changeDraftStatus(draft._id, 'IN_REVISION')();
                XSnackbar.show('Korrektur abgeschlossen!');
              },
              title: 'Korrektur abschließen',
              icon: 'check-double',
            })}
            ${deleteButton}
          </div>
        `;
      }

      if (
        draft.statusKey === 'SCHEDULED_DEPLOY' &&
        ((draft.assignedDesigner === user._id && user.hasPermission(Permissions.DesignerPermission.EDIT)) ||
          user.hasRole(Permissions.UserRoles.SUPER_ADMIN))
      ) {
        return html`
          <div class="flex gap-2 justify-end flex-wrap">
            ${AcceptButton({
              onClick: async () => {
                await changeDraftStatus(draft._id, 'IN_REVISION')();
                XSnackbar.show('Veröffentlichung abgebrochen!');
              },
              title: 'Veröffentlichung abbrechen',
              icon: 'power-off',
            })}
            ${deleteButton}
          </div>
        `;
      }

      return html` ${deleteButton} `;
    };

    const dataViewOptions = useMemo(
      () => [
        buildFullOptions({
          class: Draft,
          operation: subscribeReviewDrafts,
          view: new View({ filter }),
          customColumns: [
            {
              fieldName: 'name',
              columnName: 'Name',
              renderItem: (draft: Draft) => html`
                <d-flex space="sm">
                  <d-text>${draft.name || 'Unbenannt'}</d-text>
                  ${draft.createdBy === user._id
                    ? html` <d-text ${css({ color: '#c2c1c0' })}> Von mir erstellt </d-text> `
                    : nothing}
                  <d-text tiny grey>
                    ${(draft.updatedAt ?? draft.createdAt).toLocaleString(DateTime.DATETIME_MED)}
                  </d-text>
                </d-flex>
              `,
            },
            {
              fieldName: 'infos',
              columnName: 'Infos',
              renderItem: (draft: Draft) => html`
                <d-flex space="sm">
                  ${'name' in draft.entity
                    ? html` <d-text ${css({ fontSize: '16px', color: '#333' })}>${draft.entity.name}</d-text> `
                    : nothing}
                  ${'path' in draft.entity
                    ? html`
                        <d-text ${css({ fontSize: '12px', color: '#c2c1c0' })}> Pfad: ${draft.entity.path}</d-text>
                      `
                    : nothing}
                  ${addInfoToDraft
                    ? html`${until(addInfoToDraft(draft), html`<d-text>Lade Informationen...</d-text>`)}`
                    : nothing}
                </d-flex>
              `,
              selectionSet: {
                entity: {
                  name: 1,
                  path: 1,
                },
              },
            },
            {
              fieldName: 'description',
              columnName: 'Beschreibung',
              renderItem: (draft: Draft) => html`<d-text>${draft.description || '-'}</d-text>`,
            },
            {
              fieldName: 'status',
              columnName: 'Status',
              renderItem: (draft: Draft) => renderStatusBadge(draft.status),
            },
            {
              fieldName: 'assignedDesigner',
              columnName: 'Designer',
              columnWidth: '100px',
              renderItem: (draft: Draft) => {
                const isCreator = draft.createdBy === user._id;
                const isDesigner = user.hasPermission(Permissions.DesignerPermission.EDIT);
                const isAssigned = draft.assignedDesigner === user._id;

                return UserPicker({
                  assignedUserId: draft.assignedDesigner,
                  label: 'Designer',
                  currentUser: user,
                  availableUsers: designers,
                  isCreator,
                  onUserAssigned: async (userId: string) => {
                    await changeAssignee(draft._id, 'assignedDesigner', userId)();
                  },
                  onUserRemoved: async () => {
                    await changeAssignee(draft._id, 'assignedDesigner')();
                  },
                  isLeftAligned: false,
                  canEdit: (isDesigner && isAssigned) || (isDesigner && !draft.assignedDesigner) || isCreator,
                });
              },
            },
            {
              fieldName: 'assignedReviewer',
              columnName: 'Redakteur',
              columnWidth: '100px',
              renderItem: (draft: Draft) => {
                const isCreator = draft.createdBy === user._id;
                const isRedakteur = user.hasPermission(Permissions.RedakteurPermission.EDIT);
                const isAssigned = draft.assignedReviewer === user._id;

                return UserPicker({
                  assignedUserId: draft.assignedReviewer,
                  label: 'Redakteur',
                  currentUser: user,
                  availableUsers: reviewers,
                  isCreator,
                  onUserAssigned: async (userId: string) => {
                    await changeAssignee(draft._id, 'assignedReviewer', userId)();
                  },
                  onUserRemoved: async () => {
                    await changeAssignee(draft._id, 'assignedReviewer')();
                  },
                  isLeftAligned: false,
                  canEdit: (isRedakteur && isAssigned) || (isRedakteur && !draft.assignedReviewer) || isCreator,
                });
              },
            },
            {
              customID: 'actions',
              columnName: 'Aktionen',
              columnWidth: '150px',
              renderItem: (draft: Draft) => renderActionButtons(draft),
            },
          ],
          selectionSet: {
            assignedDesigner: 1,
            assignedReviewer: 1,
            status: {
              name: 1,
              deployAt: 1,
            },
            createdBy: 1,
            createdAt: 1,
            updatedAt: 1,
            referenceID: 1,
            name: 1,
            description: 1,
            _id: 1,
          },
          emptyState: () => html`
            <div class="alert alert-info" style="grid-column: 1 / 10">
              <d-icon solid> info </d-icon>
              <span>Keine Entwürfe zur Überprüfung vorhanden.</span>
            </div>
          `,
          loader: () => html`<span class="loading loading-spinner loading-md text-primary"></span>`,
        } as any satisfies DataViewOptions<typeof Draft>).dataViewOptions,
      ],
      [filter],
    );

    return html`
      <d-flex space="md">
        <d-text> <d-bold> Entwürfe zur Überprüfung </d-bold> </d-text>
        <d-dropdown-multiselection
          placeholder-mode="static-floating"
          placeholder="Status Filter"
          clearable
          .selectables=${Object.keys(DraftStatusKeys)}
          .renderString=${key => DraftStatusKeys[key]}
          @value-picked=${(e: ValueEvent<Array<keyof typeof DraftStatusKeys>>) => {
            filter.status = e.detail.value;
            setFilter(filter);
          }}
        >
        </d-dropdown-multiselection>
        ${DataTable({ options: dataViewOptions })}
      </d-flex>
    `;
  },
  { shadowRoot: true },
);
